import * as React from 'react';

import {
  Button,
  Col,
  ContentSection,
  Grid,
  Money,
  Row,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash2 } from 'lucide-react';

import { $remoteBillDetail } from '../../_state/billDetail.state';
import { $removePaymentFromBill } from '../../_state/paymentDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { BillLinkedPaymentRecord, BillRecord } from '../../types';
import { FinanceDetailLayout } from '../_layouts/FinanceDetailLayout';

import { BillPaymentDrawer } from './BillPaymentDrawer';

type Props = {
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ BILL TAB PAYMENTS ⚛️ ⚛️ ⚛️
//
export const BillTabPayments: React.FC<Props> = ({ disabled }) => {
  const t = useTranslations();

  // States
  const [showPaymentDrawer, setShowPaymentDrawer] = React.useState(false);

  // Atoms
  const queryResult = useAtomValue($remoteBillDetail);
  const removeFromBill = useSetAtom($removePaymentFromBill);

  // Other data hooks
  const toast = useToast();

  const bill = (queryResult?.data as { bill: BillRecord | null } | undefined)
    ?.bill;
  const refetch = queryResult?.refetch;
  const payments = bill?.payments ?? [];

  // Callbacks
  const handleRemove = React.useCallback(
    async (paymentId: string) => {
      if (!bill?.id) return;
      try {
        await removeFromBill({ paymentId, billId: bill.id });
        toast.success(t.bills.detail.messages.paymentRemoved);
        refetch?.();
      } catch (err) {
        toast.error(
          t.bills.detail.messages.removePaymentError(
            err instanceof Error ? err.message : t.messages.common.unknownError,
          ),
        );
      }
    },
    [bill?.id, removeFromBill, toast, refetch, t],
  );

  return (
    <FinanceDetailLayout className="@BillTabPayments h-auto">
      <ContentSection
        title={
          <Row xBetween yCenter>
            <h3 className="text-lg font-semibold text-neutral/90">
              {t.bills.detail.sections.payments}
            </h3>
            {!disabled && (
              <Button
                variant="outline"
                icon={<LucidePlusCircle className="size-4" />}
                onPress={() => setShowPaymentDrawer(true)}
              >
                {t.bills.detail.payments.applyPayment}
              </Button>
            )}
          </Row>
        }
      >
        <Col gap4>
          {payments.length === 0 ? (
            <div className="py-8 text-center text-neutral/60">
              {t.bills.detail.payments.empty}
            </div>
          ) : (
            <Grid className="grid-cols-[2fr_1fr_1fr_auto]">
              <div className="col-span-full grid grid-cols-subgrid border-b border-neutral/10 pb-2 text-sm font-medium text-neutral/60">
                <div>{t.bills.detail.payments.columns.paymentNumber}</div>
                <div>{t.bills.detail.payments.columns.appliedAmount}</div>
                <div>{t.bills.detail.payments.columns.date}</div>
                <div />
              </div>
              {payments.map((payment: BillLinkedPaymentRecord) => (
                <div
                  key={payment.id}
                  className="col-span-full grid grid-cols-subgrid items-center border-b border-neutral/5 py-3"
                >
                  <div className="font-medium">{payment.documentNumber}</div>
                  <div>
                    <Money
                      amount={payment.appliedAmount.amount}
                      currencyCode={payment.appliedAmount.currencyCode}
                    />
                  </div>
                  <div>{payment.transactionDate?.toString() ?? '-'}</div>
                  <div>
                    <Button
                      variant="ghost"
                      color="failure"
                      icon={<LucideTrash2 className="size-4" />}
                      onPress={() => handleRemove(payment.id)}
                      isDisabled={disabled}
                      aria-label="Remove payment"
                    />
                  </div>
                </div>
              ))}
            </Grid>
          )}
        </Col>

        {bill && (
          <BillPaymentDrawer
            isOpen={showPaymentDrawer}
            onClose={() => setShowPaymentDrawer(false)}
            bills={[bill]}
            onSuccess={() => refetch?.()}
          />
        )}
      </ContentSection>
    </FinanceDetailLayout>
  );
};
